import React, { useState, useEffect } from 'react';
import { 
    Table, TableBody, TableCell, TableContainer, TableRow, Paper, 
    TableHead, Button, TablePagination, TextField, Dialog, 
    DialogActions, DialogContent, DialogContentText, DialogTitle,
    Autocomplete, Grid, FormControlLabel, Checkbox, Box, Chip,
    IconButton, Collapse, Typography, Select, MenuItem
} from '@mui/material';
import { 
    Add as AddIcon, 
    Edit as EditIcon, 
    Delete as DeleteIcon,
    ExpandMore as ExpandMoreIcon,
    KeyboardArrowRight as KeyboardArrowRightIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    FileDownload as FileDownloadIcon,
    CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useUser } from '../contexts/UserContext';
import { useAccesos } from '../contexts/AccesosContext';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';

function GestionCuentaContable() {
    const server = process.env.REACT_APP_API_ROUTE;
    const { user } = useUser();
    const { tienePermiso } = useAccesos();

    // Función helper para formatear el usuario a 20 caracteres (requerido por BD)
    const formatUser = (username) => {
        if (!username) username = 'ADMIN';
        // Si ya tiene 20 caracteres o más, devolverlo como está (truncar a 20)
        if (username.length >= 20) return username.substring(0, 20);
        // Si tiene menos de 20, rellenar con espacios
        return username.padEnd(20, ' ');
    };

    // Estados principales
    const [cuentas, setCuentas] = useState([]);
    const [unidadesNegocio, setUnidadesNegocio] = useState([]);
    const [tiposAuxiliar, setTiposAuxiliar] = useState([]);
    const [cuentasConUnidades, setCuentasConUnidades] = useState({});
    
    // Estados del formulario
    const [descripcion, setDescripcion] = useState('');
    const [cuentaPadre, setCuentaPadre] = useState(null);
    const [idTipoAuxiliar, setIdTipoAuxiliar] = useState(null);
    const [tipoAuxiliar, setTipoAuxiliar] = useState(null);
    const [nivel, setNivel] = useState(1);
    const [requiereAuxiliar, setRequiereAuxiliar] = useState(false);
    const [activa, setActiva] = useState(true);
    const [unidadesSeleccionadas, setUnidadesSeleccionadas] = useState([]);
    
    // Estados de control
    const [editingCuenta, setEditingCuenta] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [openDialog, setOpenDialog] = useState(false);
    const [cuentaToDelete, setCuentaToDelete] = useState(null);
    const [expandedRows, setExpandedRows] = useState({});

    useEffect(() => {
        fetchCuentas();
        fetchUnidadesNegocio();
        fetchTiposAuxiliar();
        fetchTodasLasUnidadesAsignadas();
    }, []);

    // Debug: Verificar usuario al cargar
    useEffect(() => {
        if (user) {
            console.log('👤 Usuario cargado:', {
                codigoUsuario: user.codigoUsuario,
                length: user.codigoUsuario?.length,
                nombreCompleto: user.nombreCompleto
            });
        }
    }, [user]);

    // Expandir cuentas automáticamente cuando hay búsqueda
    useEffect(() => {
        if (searchQuery) {
            // Expandir todas las cuentas que coincidan o tengan hijos que coincidan
            const expandAllMatching = () => {
                const newExpandedRows = {};
                
                const expandIfMatches = (cuenta) => {
                    const matches = cuenta.descripcion?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                   cuenta.codigo?.toLowerCase().includes(searchQuery.toLowerCase());
                    
                    const hijos = getHijos(cuenta.idCuenta);
                    let anyChildMatches = false;
                    
                    hijos.forEach(hijo => {
                        if (expandIfMatches(hijo)) {
                            anyChildMatches = true;
                        }
                    });
                    
                    if (matches || anyChildMatches) {
                        newExpandedRows[cuenta.idCuenta] = true;
                        return true;
                    }
                    return false;
                };
                
                cuentas.filter(c => c.nivel === 1 || !c.idPadre).forEach(cuenta => {
                    expandIfMatches(cuenta);
                });
                
                setExpandedRows(newExpandedRows);
            };
            
            expandAllMatching();
        }
    }, [searchQuery, cuentas]);

    useEffect(() => {
        if (editingCuenta) {
            setDescripcion(editingCuenta.descripcion || '');
            const nivelCalculado = editingCuenta.idPadre ? 
                (cuentas.find(c => c.idCuenta === editingCuenta.idPadre)?.nivel || 0) + 1 : 1;
            setNivel(nivelCalculado);
            setRequiereAuxiliar(editingCuenta.requiereAuxiliar || false);
            setActiva(editingCuenta.activa !== false);
            
            setCuentaPadre(cuentas.find(c => c.idCuenta === editingCuenta.idPadre) || null);
            setTipoAuxiliar(tiposAuxiliar.find(t => t.id === editingCuenta.idTipoAuxiliar) || null);
            
            setUnidadesSeleccionadas(cuentasConUnidades[editingCuenta.idCuenta] || []);
        } else {
            limpiarFormulario();
        }
    }, [editingCuenta, cuentas, tiposAuxiliar, cuentasConUnidades]);

    // Efecto para actualizar automáticamente el código cuando cambia la cuenta padre durante la edición
    useEffect(() => {
        if (editingCuenta && cuentaPadre !== cuentas.find(c => c.idCuenta === editingCuenta.idPadre)) {
            // El padre cambió, se actualizará automáticamente en handleSubmit
            console.log('👁️ Padre de cuenta cambiado - El código se actualizará al guardar');
        }
    }, [cuentaPadre, editingCuenta, cuentas]);

    const fetchCuentas = async () => {
        try {
            const response = await fetch(`${server}/CtbPlanDeCuentas`);
            const data = await response.json();
            let cuentasData = data.$values || [];
            
            // LIMPIEZA: Remover propiedades de navegación circulares de Entity Framework
            cuentasData = cuentasData.map(cuenta => ({
                idCuenta: cuenta.idCuenta,
                codigo: cuenta.codigo,
                descripcion: cuenta.descripcion,
                nivel: cuenta.nivel,
                idPadre: cuenta.idPadre,
                idTipoCuenta: cuenta.idTipoCuenta,
                aceptaMovimientos: cuenta.aceptaMovimientos,
                requiereAuxiliar: cuenta.requiereAuxiliar,
                requiereCC: cuenta.requiereCC,
                idTipoAuxiliar: cuenta.idTipoAuxiliar,
                activa: cuenta.activa,
                path: cuenta.path,
                codigoPath: cuenta.codigoPath,
                guidImport: cuenta.guidImport,
                ordenPresentacion: cuenta.ordenPresentacion,
                ingresadoPor: cuenta.ingresadoPor,
                fechaIngreso: cuenta.fechaIngreso,
                modificacion: cuenta.modificacion,
                modificadoPor: cuenta.modificadoPor,
                fechaModificacion: cuenta.fechaModificacion
            }));
            
            // DETECCIÓN DE CORRUPCIÓN: Cuentas con auto-referencia
            const cuentasCorruptas = cuentasData.filter(c => c.idCuenta === c.idPadre);
            
            if (cuentasCorruptas.length > 0) {
                console.error('🔴 CUENTAS CORRUPTAS DETECTADAS (id_cuenta = id_padre):', {
                    total: cuentasCorruptas.length,
                    porcentaje: ((cuentasCorruptas.length / cuentasData.length) * 100).toFixed(1) + '%',
                    ejemplos: cuentasCorruptas.slice(0, 10).map(c => ({
                        id: c.idCuenta,
                        codigo: c.codigo,
                        descripcion: c.descripcion?.substring(0, 40),
                        nivel: c.nivel
                    }))
                });
                console.warn('⚠️ Estas cuentas serán filtradas automáticamente en la UI');
                console.warn('⚠️ ACCIÓN REQUERIDA: Ejecutar SQL para corregir id_padre en la base de datos');
            }
            
            console.log('✅ Plan de Cuentas cargado exitosamente:', {
                total: cuentasData.length,
                válidas: cuentasData.length - cuentasCorruptas.length,
                corruptas: cuentasCorruptas.length,
                distribución: {
                    nivel1: cuentasData.filter(c => c.nivel === 1).length,
                    nivel2: cuentasData.filter(c => c.nivel === 2).length,
                    nivel3: cuentasData.filter(c => c.nivel === 3).length,
                    nivel4: cuentasData.filter(c => c.nivel === 4).length,
                    nivel5: cuentasData.filter(c => c.nivel === 5).length,
                },
                estructura: {
                    raíz: cuentasData.filter(c => !c.idPadre || c.idPadre === null).length,
                    conPadre: cuentasData.filter(c => c.idPadre && c.idCuenta !== c.idPadre).length,
                }
            });
            
            setCuentas(cuentasData);
        } catch (error) {
            console.error('Error al cargar cuentas:', error);
        }
    };

    const fetchUnidadesNegocio = async () => {
        try {
            const response = await fetch(`${server}/CtbUnidadNegocios`);
            const data = await response.json();
            let unidades = data.$values || [];
            
            // LIMPIEZA: Remover propiedades de navegación circulares de Entity Framework
            unidades = unidades.map(u => ({
                id: u.id,
                unidadNegocio: u.unidadNegocio,
                fechaIngreso: u.fechaIngreso,
                ingresadoPor: u.ingresadoPor,
                fechaModificacion: u.fechaModificacion,
                modificadoPor: u.modificadoPor,
                modificacion: u.modificacion
            }));
            
            console.log('✅ Unidades de Negocio cargadas:', {
                total: unidades.length,
                unidades: unidades.map(u => ({
                    id: u.id,
                    nombre: u.unidadNegocio
                }))
            });
            setUnidadesNegocio(unidades);
        } catch (error) {
            console.error('Error al cargar unidades de negocio:', error);
        }
    };

    const fetchTiposAuxiliar = async () => {
        try {
            const response = await fetch(`${server}/CtbTipoAuxiliars`);
            const data = await response.json();
            setTiposAuxiliar(data.$values || []);
        } catch (error) {
            console.error('Error al cargar tipos auxiliares:', error);
        }
    };

    const fetchTodasLasUnidadesAsignadas = async () => {
        try {
            const response = await fetch(`${server}/CtbCuentasContablesXUnidadNegocios`);
            const data = await response.json();
            const asignaciones = data.$values || [];
            
            const unidadesPorCuenta = {};
            asignaciones.forEach(item => {
                if (!unidadesPorCuenta[item.idCuenta]) {
                    unidadesPorCuenta[item.idCuenta] = [];
                }
                unidadesPorCuenta[item.idCuenta].push(item.idUnidadNegocio);
            });
            
            console.log('✅ Asignaciones Cuenta-Unidad cargadas:', {
                totalAsignaciones: asignaciones.length,
                cuentasConUnidades: Object.keys(unidadesPorCuenta).length,
                ejemplos: Object.entries(unidadesPorCuenta).slice(0, 5).map(([idCuenta, unidades]) => ({
                    idCuenta,
                    unidadesIds: unidades
                }))
            });
            
            setCuentasConUnidades(unidadesPorCuenta);
        } catch (error) {
            console.error('Error al cargar unidades asignadas:', error);
        }
    };

    const generarCodigo = (padre, nivel) => {
        if (!padre) {
            // Para nivel 1, generar código secuencial: 01, 02, 03...
            const cuentasNivel1 = cuentas.filter(c => c.nivel === 1);
            const maxCodigo = Math.max(0, ...cuentasNivel1.map(c => parseInt(c.codigo) || 0));
            return String(maxCodigo + 1).padStart(2, '0');
        }
        
        // Para niveles inferiores, agregar al código del padre
        const hermanos = cuentas.filter(c => c.idPadre === padre.idCuenta);
        const maxSecuencia = Math.max(0, ...hermanos.map(c => {
            const partes = c.codigo.split('.');
            return parseInt(partes[partes.length - 1]) || 0;
        }));
        
        // Solo nivel 5 usa 3 dígitos, los demás usan 2
        // Formato: 01.01.01.02.001 = 2+1+2+1+2+1+2+1+3 = 15 caracteres (con puntos)
        const nivelCalculado = (padre.nivel || 0) + 1;
        const digitos = nivelCalculado === 5 ? 3 : 2;
        return `${padre.codigo}.${String(maxSecuencia + 1).padStart(digitos, '0')}`;
    };

    const construirPath = (padre, descripcion) => {
        if (!padre) {
            return descripcion;
        }
        return `${padre.path} - ${descripcion}`;
    };

    const construirCodigoPath = (padre, codigo) => {
        if (!padre) {
            return codigo;
        }
        return `${padre.codigoPath}.${codigo.split('.').pop()}`;
    };

    const limpiarFormulario = () => {
        setDescripcion('');
        setCuentaPadre(null);
        setTipoAuxiliar(null);
        setNivel(1);
        setRequiereAuxiliar(false);
        setActiva(true);
        setUnidadesSeleccionadas([]);
        setEditingCuenta(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!descripcion.trim()) {
            Swal.fire({
                icon: 'warning',
                title: 'Campo requerido',
                text: 'La descripción es obligatoria',
            });
            return;
        }

        if (requiereAuxiliar && !tipoAuxiliar) {
            Swal.fire({
                icon: 'warning',
                title: 'Campo requerido',
                text: 'Seleccione el tipo de auxiliar para esta cuenta',
            });
            return;
        }

        const nivelCalculado = cuentaPadre ? (cuentaPadre.nivel || 0) + 1 : 1;
        
        // Determinar si se cambió el padre (para actualizar código automáticamente)
        const padreActualDelCuenta = editingCuenta ? cuentas.find(c => c.idCuenta === editingCuenta.idPadre) : null;
        const cambioPadre = editingCuenta && padreActualDelCuenta?.idCuenta !== cuentaPadre?.idCuenta;
        
        // Generar código: regenerar si es nuevo O si cambió el padre
        const codigoGenerado = (editingCuenta && !cambioPadre) ? editingCuenta.codigo : generarCodigo(cuentaPadre, nivelCalculado);
        const pathGenerado = construirPath(cuentaPadre, descripcion);
        const codigoPathGenerado = construirCodigoPath(cuentaPadre, codigoGenerado);
        
        // Determinar tipo de cuenta según nivel
        const getTipoCuentaPorNivel = (nivel) => {
            const tipos = {
                1: 'GENERAL',
                2: 'MAYOR',
                3: 'GRUPAL',
                4: 'SUBGRUPAL',
                5: 'DETALLE'
            };
            return tipos[nivel] || 'DETALLE';
        };

        const cuenta = {
            idCuenta: editingCuenta?.idCuenta || 0,
            codigo: codigoGenerado,
            descripcion: descripcion.trim(),
            nivel: nivelCalculado,
            idPadre: cuentaPadre?.idCuenta || null,
            idTipoCuenta: null, // Se puede buscar por nombre si existe en BD
            idTipoAuxiliar: requiereAuxiliar ? (tipoAuxiliar?.id || null) : null,
            aceptaMovimientos: nivelCalculado === 5, // Solo nivel 5 acepta movimientos
            requiereAuxiliar,
            requiereCC: false, // Removido según requerimiento
            activa,
            path: pathGenerado,
            codigoPath: codigoPathGenerado,
            ordenPresentacion: 0,
            guidImport: editingCuenta?.guidImport || '00000000-0000-0000-0000-000000000000',
        };

        // Agregar campos de auditoría según el tipo de operación
        if (editingCuenta) {
            // PUT: Preservar ingresadoPor original y agregar modificadoPor
            cuenta.ingresadoPor = editingCuenta.ingresadoPor;
            cuenta.modificadoPor = formatUser(user?.codigoUsuario || 'ADMIN');
        } else {
            // POST: Solo ingresadoPor
            cuenta.ingresadoPor = formatUser(user?.codigoUsuario || 'ADMIN');
        }

        Object.keys(cuenta).forEach(key => cuenta[key] === undefined && delete cuenta[key]);

        const method = editingCuenta ? 'PUT' : 'POST';
        const endpoint = editingCuenta 
            ? `${server}/CtbPlanDeCuentas/${editingCuenta.idCuenta}` 
            : `${server}/CtbPlanDeCuentas`;

        try {
            const response = await fetch(endpoint, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(cuenta),
            });

            if (response.ok) {
                // Algunos controladores devuelven 204 No Content en PUT.
                // Evitar parsear JSON cuando no hay contenido y obtener el id de la cuenta de forma segura.
                let savedCuenta = null;
                let idCuenta = editingCuenta?.idCuenta;

                try {
                    const contentType = response.headers.get("content-type");
                    const hasJson = contentType && contentType.includes("application/json");
                    // Solo parsear JSON si realmente viene contenido JSON
                    if (hasJson) {
                        savedCuenta = await response.json();
                    }
                } catch (parseOkIgnored) {
                    // Ignorar errores de parseo cuando el backend responde 204 o sin cuerpo
                }

                if (!idCuenta && savedCuenta?.idCuenta) {
                    idCuenta = savedCuenta.idCuenta;
                }

                try {
                    // Guardar unidades de negocio asignadas
                    await guardarUnidadesNegocio(idCuenta);

                    Swal.fire({
                        icon: 'success',
                        title: 'Éxito',
                        text: `Cuenta ${editingCuenta ? 'actualizada' : 'creada'} correctamente`,
                        timer: 1500,
                        showConfirmButton: false,
                    });

                    limpiarFormulario();
                    await fetchCuentas();
                    await fetchTodasLasUnidadesAsignadas();
                } catch (unidadesError) {
                    // La cuenta se guardó pero falló la asignación de unidades
                    Swal.fire({
                        icon: 'warning',
                        title: 'Guardado Parcial',
                        html: `<div style="text-align: left;">
                            <p>La cuenta se guardó correctamente, pero hubo un problema al asignar las unidades de negocio:</p>
                            <p style="color: #e74c3c; margin-top: 10px;">${unidadesError.message || unidadesError}</p>
                            <p style="margin-top: 10px;">Puedes editar la cuenta e intentar asignar las unidades nuevamente.</p>
                        </div>`,
                        width: '600px'
                    });
                    limpiarFormulario();
                    await fetchCuentas();
                    await fetchTodasLasUnidadesAsignadas();
                }
            } else {
                // Intentar obtener el mensaje de error del servidor
                let errorMessage = 'Error al guardar la cuenta';
                try {
                    const contentType = response.headers.get("content-type");
                    if (contentType && contentType.includes("application/json")) {
                        const errorData = await response.json();
                        errorMessage = errorData.message || errorData.title || errorData.error || JSON.stringify(errorData);
                    } else {
                        const errorText = await response.text();
                        errorMessage = errorText || `Error HTTP ${response.status}`;
                    }
                } catch (parseError) {
                    errorMessage = `Error ${response.status}: ${response.statusText}`;
                }

                Swal.fire({
                    icon: 'error',
                    title: `Error ${response.status}`,
                    html: `<div style="max-height: 300px; overflow-y: auto; text-align: left;">${errorMessage}</div>`,
                    width: '600px'
                });
            }
        } catch (error) {
            console.error('Error en handleSubmit:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error de Conexión',
                html: `<div style="text-align: left;">
                    <p><strong>No se pudo guardar la cuenta</strong></p>
                    <p>Detalles: ${error.message || error}</p>
                    <p style="font-size: 0.9em; color: #666;">Verifica tu conexión con el servidor.</p>
                </div>`,
                width: '600px'
            });
        }
    };

    const guardarUnidadesNegocio = async (idCuenta) => {
        try {
            // Obtener asignaciones actuales
            const response = await fetch(`${server}/CtbCuentasContablesXUnidadNegocios`);
            const data = await response.json();
            const asignacionesActuales = (data.$values || []).filter(item => item.idCuenta === idCuenta);

            // Eliminar asignaciones que ya no están seleccionadas
            for (const asignacion of asignacionesActuales) {
                if (!unidadesSeleccionadas.includes(asignacion.idUnidadNegocio)) {
                    const deleteResponse = await fetch(`${server}/CtbCuentasContablesXUnidadNegocios/${asignacion.id}/${user?.codigoUsuario || formatUser('ADMIN')}`, {
                        method: 'DELETE',
                    });
                    if (!deleteResponse.ok) {
                        console.warn(`Error al eliminar unidad de negocio ${asignacion.idUnidadNegocio}:`, deleteResponse.status);
                    }
                }
            }

            // Agregar nuevas asignaciones
            for (const idUnidad of unidadesSeleccionadas) {
                const existe = asignacionesActuales.some(a => a.idUnidadNegocio === idUnidad);
                if (!existe) {
                    const postResponse = await fetch(`${server}/CtbCuentasContablesXUnidadNegocios`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            id: 0,
                            idUnidadNegocio: idUnidad,
                            idCuenta: idCuenta,
                            ingresadoPor: user?.codigoUsuario || formatUser('ADMIN'),
                        }),
                    });
                    if (!postResponse.ok) {
                        const errorText = await postResponse.text();
                        console.warn(`Error al agregar unidad de negocio ${idUnidad}:`, errorText);
                        throw new Error(`No se pudo asignar la unidad de negocio. ${errorText}`);
                    }
                }
            }
        } catch (error) {
            console.error('Error al guardar unidades de negocio:', error);
            throw error; // Re-lanzar para que sea manejado por la función que llamó
        }
    };

    const handleDelete = async () => {
        if (!cuentaToDelete) return;

        try {
            const username = formatUser(user?.codigoUsuario || 'ADMIN');
            
            // Determinar si se va a activar o inactivar
            const nuevoEstado = !cuentaToDelete.activa;
            
            // Preparar la cuenta con el nuevo estado
            const cuentaActualizar = {
                ...cuentaToDelete,
                activa: nuevoEstado,
                modificadoPor: username
            };

            const response = await fetch(
                `${server}/CtbPlanDeCuentas/${cuentaToDelete.idCuenta}`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(cuentaActualizar)
                }
            );

            if (response.ok) {
                Swal.fire({
                    icon: 'success',
                    title: 'Éxito',
                    text: `Cuenta ${nuevoEstado ? 'reactivada' : 'inactivada'} correctamente`,
                    timer: 1500,
                    showConfirmButton: false,
                });
                fetchCuentas();
                await fetchTodasLasUnidadesAsignadas();
            } else {
                // Capturar error de la API
                let errorMessage = `No se pudo ${nuevoEstado ? 'reactivar' : 'inactivar'} la cuenta`;
                try {
                    const contentType = response.headers.get("content-type");
                    if (contentType && contentType.includes("application/json")) {
                        const errorData = await response.json();
                        errorMessage = errorData.message || errorData.title || errorData.error || JSON.stringify(errorData);
                    } else {
                        const errorText = await response.text();
                        errorMessage = errorText || `Error HTTP ${response.status}`;
                    }
                } catch (parseError) {
                    errorMessage = `Error ${response.status}: ${response.statusText}`;
                }

                Swal.fire({
                    icon: 'error',
                    title: `Error ${response.status}`,
                    html: `<div style="max-height: 300px; overflow-y: auto; text-align: left;">
                        <p>${errorMessage}</p>
                    </div>`,
                    width: '600px'
                });
            }
        } catch (error) {
            console.error('Error en handleDelete:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error de Conexión',
                html: `<div style="text-align: left;">
                    <p><strong>No se pudo ${cuentaToDelete.activa ? 'inactivar' : 'reactivar'} la cuenta</strong></p>
                    <p>Detalles: ${error.message || error}</p>
                    <p style="font-size: 0.9em; color: #666;">Verifica tu conexión con el servidor.</p>
                </div>`,
                width: '600px'
            });
        } finally {
            setOpenDialog(false);
            setCuentaToDelete(null);
        }
    };

    const handleOpenDialog = (cuenta) => {
        setCuentaToDelete(cuenta);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setCuentaToDelete(null);
    };

    const toggleRow = (idCuenta) => {
        setExpandedRows(prev => ({
            ...prev,
            [idCuenta]: !prev[idCuenta]
        }));
    };

    const startEditRow = (cuenta) => {
        // Cargar la cuenta en el formulario para edición
        setEditingCuenta(cuenta);
        // Desplazar a la parte superior donde está el formulario
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const guardarUnidadesNegocioDirecto = async (idCuenta, unidadesNuevas) => {
        try {
            const response = await fetch(`${server}/CtbCuentasContablesXUnidadNegocios`);
            const data = await response.json();
            const asignacionesActuales = (data.$values || []).filter(item => item.idCuenta === idCuenta);

            // Eliminar asignaciones que ya no están
            for (const asignacion of asignacionesActuales) {
                if (!unidadesNuevas.includes(asignacion.idUnidadNegocio)) {
                    const deleteResponse = await fetch(`${server}/CtbCuentasContablesXUnidadNegocios/${asignacion.id}/${user?.codigoUsuario || formatUser('ADMIN')}`, {
                        method: 'DELETE',
                    });
                    if (!deleteResponse.ok) {
                        console.warn(`Error al eliminar unidad de negocio ${asignacion.idUnidadNegocio}:`, deleteResponse.status);
                    }
                }
            }

            // Agregar nuevas asignaciones
            for (const idUnidad of unidadesNuevas) {
                const existe = asignacionesActuales.some(a => a.idUnidadNegocio === idUnidad);
                if (!existe) {
                    const postResponse = await fetch(`${server}/CtbCuentasContablesXUnidadNegocios`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            id: 0,
                            idUnidadNegocio: idUnidad,
                            idCuenta: idCuenta,
                            ingresadoPor: user?.codigoUsuario || formatUser('ADMIN'),
                        }),
                    });
                    if (!postResponse.ok) {
                        const errorText = await postResponse.text();
                        console.warn(`Error al agregar unidad de negocio ${idUnidad}:`, errorText);
                        throw new Error(`No se pudo asignar la unidad de negocio. ${errorText}`);
                    }
                }
            }
        } catch (error) {
            console.error('Error al guardar unidades de negocio:', error);
            throw error; // Re-lanzar para que sea manejado por saveEditRow
        }
    };

    const getHijos = (idPadre) => {
        // IMPORTANTE: Filtrar cuentas que NO se apuntan a sí mismas
        const hijos = cuentas.filter(c => 
            c.idPadre === idPadre && c.idCuenta !== c.idPadre
        );
        
        return hijos;
    };

    const getCuentasNivel1 = () => {
        // Solo cuentas SIN padre (raíz de la jerarquía)
        return cuentas.filter(c => !c.idPadre || c.idPadre === null);
    };

    const exportarAExcel = () => {
        // Función recursiva para construir los datos tabulares
        const datosExport = [];
        const pathStack = []; // Para rastrear la ruta jerárquica
        const idCuentaStack = []; // Para evitar ciclos

        const agregarCuenta = (cuenta, nivel = 0) => {
            // Protección contra ciclos
            if (idCuentaStack.includes(cuenta.idCuenta)) {
                return;
            }

            idCuentaStack.push(cuenta.idCuenta);

            // Construir la ruta del padre (repetir en cada fila)
            const rutaPadres = pathStack.length > 0 ? pathStack.join(' > ') : '';

            // Obtener unidades de negocio para esta cuenta
            const unidadesCuenta = cuentasConUnidades[cuenta.idCuenta] || [];
            const unidadesNombre = unidadesCuenta
                .map(idUnidad => {
                    const unidad = unidadesNegocio.find(u => u.id === idUnidad);
                    return unidad ? unidad.unidadNegocio : `ID: ${idUnidad}`;
                })
                .join('; ');

            // Agregar la fila de esta cuenta
            datosExport.push({
                'Código': cuenta.codigo || '',
                'Descripción': cuenta.descripcion || '',
                'Nivel': `N${cuenta.nivel}`,
                'Acepta Movimientos': cuenta.nivel === 5 ? 'Sí' : 'No',
                'Unidades de Negocio': unidadesNombre || '-',
                'Activa': cuenta.activa !== false ? 'Sí' : 'No',
                'Ruta de Padres': rutaPadres
            });

            // Agregar el código actual a la pila
            pathStack.push(`${cuenta.codigo} - ${cuenta.descripcion}`);

            // Procesar hijos recursivamente
            const hijos = getHijos(cuenta.idCuenta);
            hijos.forEach(hijo => agregarCuenta(hijo, nivel + 1));

            // Remover de las pilas después de procesar
            pathStack.pop();
            idCuentaStack.pop();
        };

        // Procesar todas las cuentas de nivel 1
        const cuentasNivel1 = getCuentasNivel1();
        cuentasNivel1.forEach(cuenta => agregarCuenta(cuenta));

        // Crear el workbook
        const ws = XLSX.utils.json_to_sheet(datosExport);
        
        // Ajustar ancho de columnas
        const colWidths = [
            { wch: 15 },  // Código
            { wch: 30 },  // Descripción
            { wch: 10 },  // Nivel
            { wch: 18 },  // Acepta Movimientos
            { wch: 35 },  // Unidades de Negocio
            { wch: 10 },  // Activa
            { wch: 60 }   // Ruta de Padres
        ];
        ws['!cols'] = colWidths;

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Cuentas Contables');
        
        // Descargar el archivo
        const fecha = new Date().toISOString().split('T')[0];
        XLSX.writeFile(wb, `Plan_Cuentas_${fecha}.xlsx`);

        Swal.fire({
            icon: 'success',
            title: 'Exportación completada',
            text: `Se exportaron ${datosExport.length} cuentas a Excel`,
            timer: 2000,
            timerProgressBar: true
        });
    };

    const renderCuentaRow = (cuenta, nivel = 0, visitedIds = new Set()) => {
        // PROTECCIÓN CRÍTICA: Detectar ciclos
        if (visitedIds.has(cuenta.idCuenta)) {
            console.error('🔴 CICLO DETECTADO:', {
                idCuenta: cuenta.idCuenta,
                codigo: cuenta.codigo,
                nivel,
                visitados: Array.from(visitedIds)
            });
            return null;
        }
        
        // Protección contra profundidad excesiva
        if (nivel > 5) {
            console.warn('⚠️ Profundidad máxima alcanzada:', cuenta.codigo, nivel);
            return null;
        }
        
        // Agregar ID actual a visitados
        const newVisited = new Set(visitedIds);
        newVisited.add(cuenta.idCuenta);
        
        const hijos = getHijos(cuenta.idCuenta);
        const tieneHijos = hijos.length > 0;
        const isExpanded = expandedRows[cuenta.idCuenta] || false;
        const unidadesCuenta = cuentasConUnidades[cuenta.idCuenta] || [];

        return (
            <React.Fragment key={cuenta.idCuenta}>
                <TableRow sx={{
                    backgroundColor: cuenta.nivel === 1 ? '#f5f5f5' : 
                                   cuenta.nivel === 2 ? '#fafafa' : 'inherit',
                    opacity: cuenta.activa === false ? 0.5 : 1,
                    color: cuenta.activa === false ? '#999' : 'inherit',
                }}>
                    <TableCell sx={{ paddingLeft: `${nivel * 20 + 8}px` }}>
                        {tieneHijos && (
                            <IconButton size="small" onClick={() => toggleRow(cuenta.idCuenta)}>
                                {isExpanded ? <ExpandMoreIcon /> : <KeyboardArrowRightIcon />}
                            </IconButton>
                        )}
                        {!tieneHijos && <Box sx={{ width: 32, display: 'inline-block' }} />}
                        <Typography component="span" sx={{ fontWeight: cuenta.nivel < 5 ? 'bold' : 'normal' }}>
                            {cuenta.codigo}
                        </Typography>
                    </TableCell>
                    
                    <TableCell>
                        <Typography sx={{ fontWeight: cuenta.nivel < 5 ? 'bold' : 'normal' }}>
                            {cuenta.descripcion}
                        </Typography>
                    </TableCell>

                    <TableCell align="center">
                        <Chip 
                            label={`N${cuenta.nivel}`} 
                            size="small" 
                            color={cuenta.nivel === 5 ? 'primary' : 'default'}
                        />
                    </TableCell>

                    <TableCell align="center">
                        {cuenta.nivel === 5 ? '✓' : '-'}
                    </TableCell>

                    <TableCell>
                        <Box>
                            {unidadesCuenta.map(idUnidad => {
                                const unidad = unidadesNegocio.find(u => u.id === idUnidad);
                                return unidad ? (
                                    <Chip
                                        key={idUnidad}
                                        label={unidad.unidadNegocio || 'Sin nombre'}
                                        size="small"
                                        sx={{ mr: 0.5, mb: 0.5 }}
                                    />
                                ) : (
                                    <Chip
                                        key={idUnidad}
                                        label={`ID: ${idUnidad} (no encontrada)`}
                                        size="small"
                                        color="error"
                                        sx={{ mr: 0.5, mb: 0.5 }}
                                    />
                                );
                            })}
                        </Box>
                    </TableCell>

                    <TableCell align="center">
                        {cuenta.activa !== false ? '✓' : '-'}
                    </TableCell>

                    <TableCell align="right">
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={() => startEditRow(cuenta)}
                            disabled={!tienePermiso('Editar Cuenta Contable')}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                            size="small"
                            color={cuenta.activa === false ? "success" : "error"}
                            onClick={() => handleOpenDialog(cuenta)}
                            title={cuenta.activa === false ? "Reactivar cuenta" : "Inactivar cuenta"}
                            disabled={!tienePermiso('Eliminar Cuenta Contable')}
                        >
                            {cuenta.activa === false ? (
                                <CheckCircleIcon fontSize="small" />
                            ) : (
                                <DeleteIcon fontSize="small" />
                            )}
                        </IconButton>
                    </TableCell>
                </TableRow>

                {tieneHijos && isExpanded && hijos.map(hijo => renderCuentaRow(hijo, nivel + 1, newVisited))}
            </React.Fragment>
        );
    };

    // Filtrar cuentas de nivel 1 basadas en búsqueda
    const filteredCuentas = getCuentasNivel1().filter(cuenta => {
        if (!searchQuery) return true; // Sin búsqueda, mostrar todas nivel 1
        
        // Buscar en la cuenta actual
        const matchesCuenta = cuenta.descripcion?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                             cuenta.codigo?.toLowerCase().includes(searchQuery.toLowerCase());
        
        // Buscar en los hijos recursivamente
        const tieneHijoQueCoincide = (idPadre) => {
            const hijos = getHijos(idPadre);
            return hijos.some(hijo => {
                const matchesHijo = hijo.descripcion?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                   hijo.codigo?.toLowerCase().includes(searchQuery.toLowerCase());
                return matchesHijo || tieneHijoQueCoincide(hijo.idCuenta);
            });
        };
        
        return matchesCuenta || tieneHijoQueCoincide(cuenta.idCuenta);
    });

    return (
        <div className="right_col" role="main">
            <div className="">
                <div className="page-title">
                    <div className="title_left">
                        <h3>Gestión de Plan de Cuentas Contables</h3>
                    </div>
                </div>
                <div className="clearfix"></div>
                
                <div className="row">
                    <div className="col-md-12 col-sm-12">
                        <div className="x_panel">
                            <div className="x_content">
                                <br />
                
                                {/* Formulario compacto */}
                                <Paper sx={{ p: 2, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        {editingCuenta ? 'Editar Cuenta' : 'Nueva Cuenta'}
                    </Typography>
                    {editingCuenta && (
                        <Box sx={{ 
                            backgroundColor: '#e3f2fd', 
                            border: '1px solid #2196f3',
                            borderRadius: '4px',
                            p: 1.5,
                            mb: 2
                        }}>
                            <Typography variant="body2" color="primary">
                                <strong>💡 Modo Edición:</strong> Si cambias la cuenta padre, el código se actualizará automáticamente al guardar para reflejar la nueva jerarquía.
                            </Typography>
                        </Box>
                    )}
                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <Autocomplete
                                    options={cuentas
                                        .filter(c => {
                                            // Filtrar cuentas que NO sean nivel 5 (pueden ser padres)
                                            // Y si estamos editando, excluir la cuenta actual para evitar ciclos
                                            const esValidaComoPadre = c.nivel < 5;
                                            const noEsCuentaActual = !editingCuenta || c.idCuenta !== editingCuenta.idCuenta;
                                            return esValidaComoPadre && noEsCuentaActual;
                                        })
                                        .sort((a, b) => {
                                            // Ordenar por código alfanumérico
                                            return (a.codigo || '').localeCompare(b.codigo || '', undefined, { numeric: true });
                                        })
                                    }
                                    getOptionLabel={(option) => `${option.codigo || ''} - ${option.descripcion || ''}`}
                                    value={cuentaPadre}
                                    onChange={(e, newValue) => {
                                        setCuentaPadre(newValue);
                                        setNivel(newValue ? (newValue.nivel || 0) + 1 : 1);
                                    }}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Cuenta Padre" fullWidth />
                                    )}
                                    renderOption={(props, option) => (
                                        <li 
                                            {...props} 
                                            key={option.idCuenta}
                                            style={{ 
                                                paddingLeft: `${(option.nivel - 1) * 20 + 16}px`,
                                                backgroundColor: option.nivel === 1 ? '#f5f5f5' : 'transparent'
                                            }}
                                        >
                                            <strong style={{ marginRight: 8, color: '#1976d2' }}>N{option.nivel}</strong>
                                            {option.codigo} - {option.descripcion}
                                        </li>
                                    )}
                                    filterOptions={(options, state) => {
                                        // Filtrar por texto de búsqueda
                                        const inputValue = state.inputValue.toLowerCase();
                                        if (!inputValue) return options;
                                        
                                        return options.filter(option => 
                                            (option.codigo || '').toLowerCase().includes(inputValue) ||
                                            (option.descripcion || '').toLowerCase().includes(inputValue)
                                        );
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Descripción"
                                    value={descripcion}
                                    onChange={(e) => setDescripcion(e.target.value)}
                                    required
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Nivel"
                                    value={`Nivel ${nivel} (${nivel === 5 ? 'DETALLE - Acepta movimientos' : 'AGRUPACIÓN'})`}
                                    disabled
                                    size="small"
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                    <Autocomplete
                                        multiple
                                        options={unidadesNegocio}
                                        getOptionLabel={(option) => option.unidadNegocio || 'Sin nombre'}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        value={unidadesNegocio.filter(u => unidadesSeleccionadas.includes(u.id))}
                                        onChange={(e, newValue) => {
                                            setUnidadesSeleccionadas(newValue.map(u => u.id));
                                        }}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Unidades de Negocio" fullWidth />
                                        )}
                                        renderTags={(value, getTagProps) =>
                                            value.map((option, index) => (
                                                <Chip
                                                    key={option.id}
                                                    label={option.unidadNegocio || 'Sin nombre'}
                                                    {...getTagProps({ index })}
                                                    size="small"
                                                />
                                            ))
                                        }
                                        sx={{ flex: 1 }}
                                    />
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => {
                                            if (unidadesSeleccionadas.length === unidadesNegocio.length) {
                                                setUnidadesSeleccionadas([]);
                                            } else {
                                                setUnidadesSeleccionadas(unidadesNegocio.map(u => u.id));
                                            }
                                        }}
                                        sx={{ minWidth: '100px', height: '40px', mt: 0.5 }}
                                    >
                                        {unidadesSeleccionadas.length === unidadesNegocio.length ? 'Limpiar' : 'Todas'}
                                    </Button>
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={requiereAuxiliar}
                                            onChange={(e) => setRequiereAuxiliar(e.target.checked)}
                                        />
                                    }
                                    label="Requiere Auxiliar"
                                />
                            </Grid>

                            {requiereAuxiliar && (
                                <Grid item xs={12} md={3}>
                                    <Autocomplete
                                        options={tiposAuxiliar}
                                        getOptionLabel={(option) => option.tipoAuxiliar || option.TipoAuxiliar || ''}
                                        value={tipoAuxiliar}
                                        onChange={(e, newValue) => setTipoAuxiliar(newValue)}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Tipo de Auxiliar" fullWidth size="small" />
                                        )}
                                    />
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    startIcon={editingCuenta ? <EditIcon /> : <AddIcon />}
                                    sx={{ mr: 1 }}
                                    disabled={!tienePermiso('Editar Cuenta Contable')}
                                >
                                    {editingCuenta ? 'Actualizar' : 'Guardar'}
                                </Button>
                                {editingCuenta && (
                                    <Button
                                        variant="outlined"
                                        onClick={limpiarFormulario}
                                    >
                                        Cancelar
                                    </Button>
                                )}
                            </Grid>
                        </Grid>
                    </form>
                                </Paper>

                                <div className="ln_solid" />

                                {/* Tabla de cuentas jerárquica */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1 }}>
                                    <Typography variant="h6">Cuentas Contables - Edición en Línea</Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<FileDownloadIcon />}
                                            onClick={exportarAExcel}
                                            color="success"
                                        >
                                            Exportar a Excel
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => {
                                                const allIds = {};
                                                cuentas.forEach(c => allIds[c.idCuenta] = true);
                                                setExpandedRows(allIds);
                                            }}
                                        >
                                            Expandir Todo
                                        </Button>
                                    </Box>
                                </Box>
                
                                <div className="row">
                                    <div className="col-md-12">
                                        <TextField
                                            fullWidth
                                            variant="outlined"
                                            label="Buscar cuenta"
                                            placeholder="Buscar por código o descripción..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            size="small"
                                            style={{ marginBottom: '15px' }}
                                        />

                                        <div className="table-responsive">
                                            <TableContainer component={Paper}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ minWidth: 200 }}>Código</TableCell>
                                            <TableCell sx={{ minWidth: 300 }}>Descripción</TableCell>
                                            <TableCell align="center" sx={{ minWidth: 80 }}>Nivel</TableCell>
                                            <TableCell align="center" sx={{ minWidth: 100 }}>Acepta Mov.</TableCell>
                                            <TableCell sx={{ minWidth: 250 }}>Unidades de Negocio</TableCell>
                                            <TableCell align="center" sx={{ minWidth: 80 }}>Activa</TableCell>
                                            <TableCell align="right" sx={{ minWidth: 120 }}>Acciones</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredCuentas.map((cuenta) => renderCuentaRow(cuenta, 0))}
                                    </TableBody>
                                </Table>
                                            </TableContainer>
                                        </div>

                                        <Dialog open={openDialog} onClose={handleCloseDialog}>
                            <DialogTitle>
                                {cuentaToDelete?.activa === false ? 'Confirmar reactivación' : 'Confirmar inactivación'}
                            </DialogTitle>
                            <DialogContent>
                                <DialogContentText>
                                    {cuentaToDelete?.activa === false ? (
                                        <>
                                            ¿Está seguro de reactivar la cuenta "{cuentaToDelete?.descripcion}"?
                                            La cuenta volverá a estar disponible para su uso.
                                        </>
                                    ) : (
                                        <>
                                            ¿Está seguro de inactivar la cuenta "{cuentaToDelete?.descripcion}"?
                                            La cuenta se marcará como inactiva pero permanecerá en el sistema.
                                        </>
                                    )}
                                </DialogContentText>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={handleCloseDialog} color="primary">
                                    Cancelar
                                </Button>
                                <Button 
                                    onClick={handleDelete} 
                                    color={cuentaToDelete?.activa === false ? "success" : "warning"} 
                                    autoFocus
                                    disabled={!tienePermiso('Eliminar Cuenta Contable')}
                                >
                                    {cuentaToDelete?.activa === false ? 'Reactivar' : 'Inactivar'}
                                </Button>
                            </DialogActions>
                                        </Dialog>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default GestionCuentaContable;